import { useQuery, useMutation, useQueryClient } from 'react-query'
import { request } from 'Features/auth/axios-client'

const fetchSchedulerSOR = async (API: string) => {
  const data = await request({
    url: `/scheduler/${API}/sor`,
  })
  return data
}

export const useScheduleSOR = (API: string) => {
  return useQuery([API], () => fetchSchedulerSOR(API), {
    select: ({ data }) => data,
    refetchOnWindowFocus: false,
    staleTime: 0,
  })
}

// Returns the full response payload (data + predicate + availableLines + tableExists).
// Used by the synchronized-lines tab which needs metadata beyond the rows themselves.
export const useScheduleSORFull = <T = unknown>(API: string) => {
  return useQuery<
    {
      data: {
        data: T[]
        predicate?: unknown
        availableLines?: string[]
        tableExists?: boolean
      }
    },
    unknown,
    {
      data: T[]
      predicate?: unknown
      availableLines?: string[]
      tableExists?: boolean
    }
  >([API], () => fetchSchedulerSOR(API), {
    select: (response) => response.data,
    refetchOnWindowFocus: false,
  })
}

// Write PUT request to update production requirements

interface UpdateSchedulerSORParams<T> {
  data: Record<number, T>
  API: string
}
const updateSchedulerSOR = async <T = unknown>({
  data,
  API,
}: UpdateSchedulerSORParams<T>) => {
  const response = await request({
    method: 'put',
    data,
    url: `/scheduler/${API}/sor`,
  })
  return response
}

export const useUpdateSchedulerSOR = <T = unknown>(
  onSuccess: () => void,
  onFailure: () => void
) => {
  const queryClient = useQueryClient()
  return useMutation<unknown, unknown, UpdateSchedulerSORParams<T>>(
    updateSchedulerSOR,
    {
      onSuccess: (_, variables) => {
        onSuccess()
        queryClient.invalidateQueries([variables.API])
      },
      onError: (error) => {
        console.log('error', error)
        onFailure()
      },
    }
  )
}

type UploadFileParams = {
  file: File
  API: string
}

const uploadScheduleDataFile = async ({ file, API }: UploadFileParams) => {
  const formData = new FormData()
  formData.append('file', file)
  const { data } = await request({
    method: 'post',
    data: formData,
    url: `/scheduler/${API}/sor`,
  })
  return data
}

interface DeleteSchedulerSORParams<T> {
  rows: T[]
  API: string
}

const deleteSchedulerSOR = async <T = unknown>({
  rows,
  API,
}: DeleteSchedulerSORParams<T>) => {
  const response = await request({
    method: 'delete',
    data: rows,
    url: `/scheduler/${API}/sor`,
  })
  return response
}

export const useDeleteSchedulerSOR = <T = unknown>(
  onSuccess: () => void,
  onFailure: () => void
) => {
  const queryClient = useQueryClient()
  return useMutation<unknown, unknown, DeleteSchedulerSORParams<T>>(
    deleteSchedulerSOR,
    {
      onSuccess: (_, variables) => {
        onSuccess()
        queryClient.invalidateQueries([variables.API])
      },
      onError: (error) => {
        console.log('error', error)
        onFailure()
      },
    }
  )
}

export const useUploadScheduleDataFile = (
  onSuccess: (data?: any) => void,
  onFailure: (error?: any) => void
) => {
  const queryClient = useQueryClient()
  return useMutation(uploadScheduleDataFile, {
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries([variables.API])
      onSuccess(data.merge_info)
    },
    onError: (error) => {
      console.error('Upload error:', error)
      onFailure(error)
    },
  })
}
